import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { URL_MAP } from '../../shared/urlMap';
import { environment as ENV } from '../../../environments/environment';
import { ContratoContratanteCabeceraDTO } from '../../models/contrato-afilmed/contrato-contratante-cabecera.model';

export interface DatosAfiliadoPagadorRequest {
  prepaga: string;
  contra?: string;
  docu_tipo?: string;
  docu_nro?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DatosAfiliadoPagadorService {
  private apiUrl = `${ENV.HOST}${URL_MAP.API.DATOS_AFILIADO_PAGADOR}`;

  constructor(private http: HttpClient) {}

  getDatosAfiliadoPagador(
    params: DatosAfiliadoPagadorRequest,
  ): Observable<ContratoContratanteCabeceraDTO | null> {
    const prepaga = String(params?.prepaga ?? '').trim();
    const contra = String(params?.contra ?? '').trim();
    if (!prepaga || !contra) {
      return of(null);
    }
    
    const body = {
      jsonParametros: JSON.stringify({
        prepaga,
        contra,
        docu_tipo: String(params.docu_tipo ?? '').trim(),
        docu_nro: String(params.docu_nro ?? '').trim(),
      }),
    };

    return this.http.post<unknown>(this.apiUrl, body).pipe(
      map((raw) => this.normalizarRespuesta(raw)),
      catchError(() => of(null)),
    );
  }

  private normalizarRespuesta(
    raw: unknown,
  ): ContratoContratanteCabeceraDTO | null {
    if (raw == null) {
      return null;
    }
    let row: any = raw;
    if (Array.isArray(row)) {
      row = row[0];
    }
    if (row && typeof row === 'object' && 'data' in row && row.data != null) {
      row = Array.isArray(row.data) ? row.data[0] : row.data;
    }
    if (
      row &&
      typeof row === 'object' &&
      'content' in row &&
      row.content != null
    ) {
      row = Array.isArray(row.content) ? row.content[0] : row.content;
    }
    if (!row || typeof row !== 'object') {
      return null;
    }

    const str = (...keys: string[]): string | undefined => {
      for (const k of keys) {
        if (k in row) {
          const v = row[k];
          if (v === null || v === undefined) {
            return undefined;
          }
          const t = String(v).trim();
          if (t) {
            return t;
          }
        }
      }
      return undefined;
    };

    const out: ContratoContratanteCabeceraDTO = {
      tipoDocumento: str('docu_tipo'),
      numeroDocumento: str('docu_nro'),
      primerApellido: str('ape'),
      segundoApellido: str('ape2') ?? null,
      primerNombre: str('nombre'),
      segundoNombre: str('nombre2') ?? null,
      fechaNacimiento: str('naci_fecha'),
      genero: str('sexo') ?? null,
      nacionalidad: str('nacio') ?? null,
      estadoCivil: str('civil_esta') ?? null,
      estratoSocial: str('estrato_social') ?? null,
      profesion: str('profesion') ?? null,
      ocupacion: str('ocupacion') ?? null,
      email: str('email'),
      telefonoFijo: str('telefono_residencia'),
      telefonoCelular: str('celular'),
      tipoVia: str('tipo_via') ?? null,
      numeroVia: str('numero') ?? null,
      numeroPlaca: str('numero_placa') ?? null,
      complementoDireccion: str('complemento') ?? null,
      barrio: str('barrio') ?? null,
      departamento: str('departamento') ?? null,
      municipio: str('municipio') ?? null,
    };

    // Para este endpoint, retornar siempre el objeto mapeado.
    // El componente decide qué campos tomar/mezclar.
    return out;
  }
}
